import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Check, Lock, ArrowRight, Heart, Ticket } from "lucide-react";
import TreeMark from "@/components/TreeMark";

const FEATURES = [
  { title: "Funeral wishes chapter", desc: "Guidance to help you clearly share what you want your family to know and to set out your express wishes for your funeral and send‑off." },
  { title: "Your admin system chapter", desc: "A gentle framework for organising accounts, executors, solicitors and where the will lives." },
  { title: "Letters to loved ones chapter", desc: "The most precious part — the words your family will read a hundred times." },
  { title: "Plan Builder", desc: "Eight sections. Save online. Export as PDF. Update whenever you like." },
  { title: "AI Companion (Claude)", desc: "A warm assistant that answers your questions in plain UK English, any time." },
];

export default function Upgrade() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redeem-code state
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState("");
  const [redeemErr, setRedeemErr] = useState("");

  const startCheckout = async (promo = null) => {
    setError("");
    if (!user) {
      navigate("/register", { state: { from: "/upgrade" } });
      return;
    }
    if (user.is_premium) {
      navigate("/dashboard");
      return;
    }
    setLoading(true);
    try {
      const body = {
        product_id: "lifetime",
        origin_url: window.location.origin,
      };
      if (promo) body.promo_code = promo;
      const { data } = await api.post("/billing/checkout", body);
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e));
      setLoading(false);
    }
  };

  const redeemCode = async (e) => {
    e?.preventDefault?.();
    setRedeemErr(""); setRedeemMsg("");
    if (!user) {
      navigate("/register", { state: { from: "/upgrade" } });
      return;
    }
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/billing/redeem", { code: code.trim() });
      // Partial-discount code → send them to Stripe with the code applied
      if (data?.requires_payment) {
        setRedeemMsg(data.message || `Discount applied — continuing to checkout.`);
        setTimeout(() => { startCheckout(code.trim()); }, 400);
        return;
      }
      setRedeemMsg(data.message || "Access unlocked.");
      // Refresh /me so navbar & routes update
      await api.get("/auth/me");
      setTimeout(() => { window.location.href = "/dashboard"; }, 900);
    } catch (e) {
      setRedeemErr(formatApiError(e));
    } finally { setRedeeming(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-20">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <TreeMark size={56} tone="dark" className="mb-6" />
        <h1 className="font-serif text-4xl md:text-5xl text-[#2D332D] leading-tight mb-4">
          Unlock the full plan.
        </h1>
        <p className="text-[#576056] text-lg leading-relaxed">
          Everything about being bereaved is free — and always will be. Preparing your own affairs is a small labour of love, so we charge a one-time £29 to cover the tools that help you build it.
        </p>
      </div>

      <div className="grid md:grid-cols-5 gap-8 items-start">
        <div className="md:col-span-3 space-y-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="card p-5 md:p-6 flex gap-4">
              <div className="w-10 h-10 rounded-full bg-[#E9ECE8] flex items-center justify-center text-[#6B7F62] flex-shrink-0">
                <Check size={16} strokeWidth={2.25} />
              </div>
              <div>
                <div className="font-serif text-xl text-[#2D332D] leading-snug">{f.title}</div>
                <div className="text-[#576056] text-[15px] leading-relaxed mt-1">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="md:col-span-2 md:sticky md:top-24">
          <div className="card overflow-hidden">
            <div className="bg-[#2D332D] px-6 py-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#3D453D] flex items-center justify-center text-[#D1A793]">
                <Lock size={17} strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#9EAD94]">Lifetime access</div>
                <div className="font-serif text-lg text-white leading-tight">£29 · one-time</div>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-serif text-5xl text-[#2D332D]">£29</span>
                <span className="text-sm text-[#8A9488]">GBP · once</span>
              </div>
              <ul className="space-y-2 text-[14px] text-[#576056] mb-6">
                <li className="flex gap-2"><Check size={14} className="text-[#6B7F62] mt-0.5" /> All 5 preparation chapters</li>
                <li className="flex gap-2"><Check size={14} className="text-[#6B7F62] mt-0.5" /> Full Plan Builder with PDF export</li>
                <li className="flex gap-2"><Check size={14} className="text-[#6B7F62] mt-0.5" /> Unlimited AI companion chat</li>
                <li className="flex gap-2"><Check size={14} className="text-[#6B7F62] mt-0.5" /> Free updates for life</li>
                <li className="flex gap-2"><Check size={14} className="text-[#6B7F62] mt-0.5" /> No subscription, ever</li>
              </ul>

              {user?.is_premium ? (
                <div className="bg-[#E9ECE8] border border-[#9EAD94] rounded-xl px-4 py-3 text-sm text-[#2D332D] flex items-center gap-2 mb-3">
                  <Heart size={15} className="text-[#6B7F62]" /> Your account already has lifetime access.
                </div>
              ) : (
                <>
                  <button
                    data-testid="upgrade-checkout-btn"
                    onClick={startCheckout}
                    disabled={loading}
                    className="btn-primary w-full justify-center"
                  >
                    {loading ? "Opening secure checkout…" : "Purchase lifetime access"} {!loading && <ArrowRight size={17} />}
                  </button>

                  {/* Discount / promo code */}
                  <div className="mt-4 pt-4 border-t border-[#EAE6E1]">
                    {!showCode ? (
                      <button
                        type="button"
                        onClick={() => setShowCode(true)}
                        data-testid="reveal-promo-btn"
                        className="text-sm text-[#6B7F62] hover:underline inline-flex items-center gap-1.5"
                      >
                        <Ticket size={14} /> Have a code?
                      </button>
                    ) : (
                      <form onSubmit={redeemCode} className="space-y-3" data-testid="promo-form">
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#8A9488]">Discount code</label>
                        <input
                          type="text"
                          value={code}
                          onChange={(e) => setCode(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          data-testid="promo-input"
                          className="input"
                          autoFocus
                        />
                        <button
                          type="submit"
                          disabled={redeeming || !code.trim()}
                          data-testid="promo-submit"
                          className="btn-secondary w-full justify-center text-sm"
                        >
                          {redeeming ? "Checking…" : "Redeem code"}
                        </button>
                        {redeemMsg && <div data-testid="promo-success" className="text-sm text-[#4A6E55] bg-[#E9ECE8] border border-[#9EAD94] rounded-xl px-4 py-3">{redeemMsg}</div>}
                        {redeemErr && <div data-testid="promo-error" className="text-sm text-[#B05A5A]">{redeemErr}</div>}
                      </form>
                    )}
                  </div>
                </>
              )}

              {error && <div data-testid="upgrade-error" className="text-sm text-[#B05A5A] mt-3">{error}</div>}

              <p className="text-xs text-[#8A9488] mt-4 leading-relaxed">
                Secure payment by Stripe. All bereavement content and the first two preparation chapters remain free forever.
              </p>
              {!user && (
                <p className="text-xs text-[#576056] mt-3">
                  <Link to="/login" className="underline hover:text-[#6B7F62]">Already have an account? Sign in first.</Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
